import React, { MouseEventHandler, useEffect, useMemo, useState } from 'react';
import useStyles from './styles';
import { PostCard, AdSpace } from 'molecules';
import { PostCardInfo } from 'types/post';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Skeleton } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import type { Theme } from '@mui/material/styles';
import clsx from 'clsx';
import { reportError } from 'lib/errors';
import { Button } from '@elevator/ui';
import { LoadingError } from 'atoms';

/** Last item in an odd-length chunk leaves one cell empty at `sm` (2 columns); span row and center a single-column-width card. */
function oddLastMainGridItem(chunkLength: number, indexInChunk: number): boolean {
  return chunkLength % 2 === 1 && indexInChunk === chunkLength - 1;
}

const oddLastItemGridSx = {
  display: 'flex',
  justifyContent: 'center',
};

/** Match one `sm` column width so 16:9 image matches other cards; `spacing={2}` gap matches Grid2. */
const oddLastMainCardMaxWidthSx = (theme: Theme) => ({
  width: '100%',
  maxWidth: {
    xs: '100%',
    sm: `calc((100% - ${theme.spacing(2)}) / 2)`,
    md: '100%',
  },
});

/**
 * PostGrid Component
 *
 * Displays a grid of post cards with optional ad injection. Supports
 * both main and small variants with different layouts. Handles loading states,
 * error states, and "Load More" pagination. Manages ad space placement within
 * the post grid.
 *
 * Features:
 * - Post cards grid display
 * - Main and small layout variants
 * - Ad injection support
 * - Loading and error states
 * - Load more pagination
 * - Loading skeleton states
 *
 * Used in:
 * - PopularPostList (small variant)
 * - Home page main content (main variant)
 * - Search page (small variant, post results)
 *
 * Expects parent to wrap in <section> with <SectionHeader> as needed.
 *
 * @param props - Component props
 * @param props.posts - Array of post card information
 * @param props.error - Error object if posts failed to load
 * @param props.onLoadMore - Function called when "Load More" button is clicked
 * @param props.loadLimit - Maximum number of posts to display
 * @param props.loadingMore - Boolean indicating if more posts are loading
 * @param props.isLoading - Boolean indicating if initial posts are loading
 * @param props.variant - Layout variant: 'main' or 'small'
 * @param props.adUnitPrefix - Prefix for ad unit IDs when injecting ads
 */
const PostGrid = (props: PostGridPropTypes) => {
  const {
    posts,
    error,
    onLoadMore,
    loadLimit = 9,
    loadingMore,
    isLoading,
    variant = 'main',
    adUnitPrefix,
    lcpPriorityCount = 1,
  } = props;
  const { classes } = useStyles();

  // Apollo sets loadingMore=false before parent layouts expand displayedPosts (displayLimit
  // updates after fetchMore resolves). Keep the loader until posts.length actually grows,
  // or until the fetch finishes with no new posts.
  const [awaitingPosts, setAwaitingPosts] = useState(() => Boolean(loadingMore));
  const [postsLengthBeforeLoad, setPostsLengthBeforeLoad] = useState(() => posts.length);
  const [prevLoadingMore, setPrevLoadingMore] = useState(loadingMore);

  if (loadingMore !== prevLoadingMore) {
    setPrevLoadingMore(loadingMore);
    if (loadingMore) {
      setPostsLengthBeforeLoad(posts.length);
      setAwaitingPosts(true);
    }
  }

  if (awaitingPosts && posts.length > postsLengthBeforeLoad) {
    setAwaitingPosts(false);
  }

  useEffect(() => {
    if (!loadingMore && awaitingPosts && posts.length <= postsLengthBeforeLoad) {
      const timeoutId = window.setTimeout(() => {
        setAwaitingPosts(false);
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }
  }, [loadingMore, awaitingPosts, posts.length, postsLengthBeforeLoad]);

  const showLoadMoreLoader = Boolean(loadingMore || awaitingPosts);

  // Memoize content to prevent unnecessary re-renders when posts array reference changes
  // but data is the same. This prevents images from unmounting/remounting on re-renders.
  const content = useMemo(() => {
    if (error) {
      return (
        <Grid2 container spacing={2} className={classes.gridContainer}>
          <Grid2 size={12}>
            <LoadingError reportError={reportError} error={error} failedTarget="posts" />
          </Grid2>
        </Grid2>
      );
    }

    if (posts.length === 0) {
      if (isLoading) {
        const mainGridSize = (index: number) =>
          variant === 'small'
            ? { xs: 12 }
            : oddLastMainGridItem(loadLimit, index)
              ? { xs: 12, sm: 12, md: 4 }
              : { xs: 12, sm: 6, md: 4 };

        return (
          <Grid2 container spacing={2} className={classes.gridContainer}>
            {Array.from({ length: loadLimit }, (_, index) => (
              <Grid2
                key={index}
                size={mainGridSize(index)}
                sx={
                  variant === 'main' && oddLastMainGridItem(loadLimit, index)
                    ? oddLastItemGridSx
                    : undefined
                }
              >
                {variant === 'main' && oddLastMainGridItem(loadLimit, index) ? (
                  <Box sx={oddLastMainCardMaxWidthSx}>
                    <Skeleton variant="rectangular" width="100%" animation="pulse">
                      <Box className={classes.skeletonContainer} />
                    </Skeleton>
                  </Box>
                ) : (
                  <Skeleton variant="rectangular" width="100%" animation="pulse">
                    <Box className={classes.skeletonContainer} />
                  </Skeleton>
                )}
              </Grid2>
            ))}
          </Grid2>
        );
      } else {
        return (
          <Grid2 container spacing={2} className={classes.gridContainer}>
            <Grid2
              size={12}
              className={clsx(classes.centeredContainer, classes.contentHeight)}
            >
              <Typography>No posts found</Typography>
            </Grid2>
          </Grid2>
        );
      }
    }

    const postChunks: PostCardInfo[][] = [];
    for (let i = 0; i < posts.length; i += loadLimit) {
      postChunks.push(posts.slice(i, i + loadLimit));
    }

    return (
      <Stack className={classes.chunkGap}>
        {postChunks.map((chunk, chunkIndex) => {
          const showAd = Boolean(adUnitPrefix) && chunkIndex < postChunks.length - 1;
          const adIndex = chunkIndex + 1;

          return (
            <Stack
              key={`chunk-${chunk[0]?.slug ?? chunkIndex}`}
              className={classes.chunkGap}
            >
              <Grid2 container spacing={2} className={classes.gridContainer}>
                {chunk.map((data, indexInChunk) => {
                  const globalIndex = chunkIndex * loadLimit + indexInChunk;
                  const gridSize =
                    variant === 'small'
                      ? { xs: 12 }
                      : oddLastMainGridItem(chunk.length, indexInChunk)
                        ? { xs: 12, sm: 12, md: 4 }
                        : { xs: 12, sm: 6, md: 4 };
                  const wrapOddLast =
                    variant === 'main' && oddLastMainGridItem(chunk.length, indexInChunk);

                  return (
                    <Grid2
                      key={data.slug}
                      size={gridSize}
                      sx={wrapOddLast ? oddLastItemGridSx : undefined}
                    >
                      {wrapOddLast ? (
                        <Box sx={oddLastMainCardMaxWidthSx}>
                          <PostCard
                            {...data}
                            variant={variant}
                            priority={globalIndex < lcpPriorityCount}
                          />
                        </Box>
                      ) : (
                        <PostCard
                          {...data}
                          variant={variant}
                          priority={globalIndex < lcpPriorityCount}
                        />
                      )}
                    </Grid2>
                  );
                })}
              </Grid2>
              {showAd ? (
                <Box
                  component="aside"
                  aria-label="Advertisement"
                  data-component="PostGrid-Ad"
                >
                  <AdSpace
                    type="banner-ad"
                    adUnit={`${adUnitPrefix}-${adIndex}`}
                    size="responsive"
                  />
                </Box>
              ) : null}
            </Stack>
          );
        })}
      </Stack>
    );
  }, [
    posts,
    variant,
    adUnitPrefix,
    loadLimit,
    error,
    isLoading,
    classes,
    lcpPriorityCount,
  ]);

  return (
    <Box data-component="PostGrid" sx={{ display: 'block' }}>
      {content}
      {onLoadMore && (
        <Stack
          direction="column"
          alignItems="center"
          className={classes.loadMoreButtonContainer}
        >
          {showLoadMoreLoader && (
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              className={classes.loadingMoreContainer}
            >
              <CircularProgress />
            </Stack>
          )}
          {!showLoadMoreLoader && posts.length > 0 && !error && (
            <Button
              onClick={onLoadMore}
              variant="outlined"
              className={classes.loadMoreButton}
              fullWidth
              disabled={posts.length % loadLimit !== 0}
            >
              Load More
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

export type PostGridPropTypes = {
  posts: Array<PostCardInfo>;
  error?: Error | null;
  onLoadMore: MouseEventHandler;
  loadLimit?: number;
  loadingMore?: boolean;
  isLoading: boolean;
  variant?: 'main' | 'small';
  adUnitPrefix?: string;
  /** First N cards pass `priority` to PostCard (default 1). Increase when several above-the-fold images share the same src or Next dev LCP warns. */
  lcpPriorityCount?: number;
};

export default PostGrid;
